import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

interface Kpi {
  window: "daily" | "weekly" | "monthly";
  from: string;
  to: string;
  totals: {
    walkIns: number;
    campScreenings: number;
    phrsGenerated: number;
    whatsappSent: number;
    subscriptions: number;
    revenueInr: number;
    conversionRate: number;
  };
  tierMix: { basic: number; advanced: number; premium: number };
  series: Array<{
    kpiDate: string;
    walkInsCount: number | null;
    walkInSubsCount: number | null;
    phrsGenerated: number | null;
    revenueInrPaise: number | null;
  }>;
  generatedAt: string;
}

const TABS: Array<{ key: Kpi["window"]; label: string; days: number }> = [
  { key: "daily", label: "Last 7 days", days: 7 },
  { key: "weekly", label: "Last 28 days", days: 28 },
  { key: "monthly", label: "Last 90 days", days: 90 },
];

function Kpis() {
  const [tab, setTab] = useState<Kpi["window"]>("daily");
  const [data, setData] = useState<Kpi | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    apiFetch<Kpi>(`/api/kpis?window=${tab}`)
      .then(setData)
      .catch((e: Error) => setErr(e.message));
  }, [tab]);

  return (
    <div className="space-y-4">
      <div className="panel">
        <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">
          Screen 9
        </div>
        <h1 className="text-2xl font-semibold mb-3">KPIs</h1>
        <div className="flex gap-1 border-b border-slate-200 -mb-px">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm border-b-2 ${
                tab === t.key
                  ? "border-slate-900 text-slate-900 font-medium"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="panel text-red-600">{err}</div>}
      {!data ? (
        <div className="panel text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Tile label="Walk-ins" value={data.totals.walkIns} />
            <Tile label="Camp screenings" value={data.totals.campScreenings} />
            <Tile label="Subscriptions" value={data.totals.subscriptions} tone="ok" />
            <Tile
              label="Revenue (₹)"
              value={data.totals.revenueInr.toLocaleString()}
              tone="ok"
            />
          </div>
          <div className="grid grid-cols-4 gap-3">
            <Tile label="PHRs generated" value={data.totals.phrsGenerated} />
            <Tile label="WhatsApp sent" value={data.totals.whatsappSent} />
            <Tile
              label="Walk-in conv. rate"
              value={`${Math.round(data.totals.conversionRate * 100)}%`}
            />
            <Tile
              label="Tier mix"
              value={
                data.tierMix.basic + data.tierMix.advanced + data.tierMix.premium
              }
              sub={`B${data.tierMix.basic} · A${data.tierMix.advanced} · P${data.tierMix.premium}`}
            />
          </div>

          <div className="panel">
            <h3 className="text-sm font-medium text-slate-700 mb-2">
              Daily history ({data.from} → {data.to})
            </h3>
            {data.series.length === 0 ? (
              <div className="text-xs text-slate-400">
                No daily rollup yet — cron-worker runs at 19:00 UTC.
              </div>
            ) : (
              <Sparkline series={data.series} />
            )}
          </div>

          <div className="text-xs text-slate-400">
            Generated {new Date(data.generatedAt).toLocaleString()} · cached 5 min
          </div>
        </>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: number | string;
  sub?: string;
  tone?: "ok";
}) {
  const cls = tone === "ok" ? "border-green-200 bg-green-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border p-4 shadow-sm ${cls}`}>
      <div className="text-3xl font-semibold">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Sparkline({
  series,
}: {
  series: Kpi["series"];
}) {
  const width = 700;
  const height = 80;
  const padding = 8;
  const max = Math.max(1, ...series.map((s) => s.walkInsCount ?? 0));
  const stepX = (width - padding * 2) / Math.max(1, series.length - 1);
  const points = series
    .map((s, i) => {
      const x = padding + i * stepX;
      const y = height - padding - ((s.walkInsCount ?? 0) / max) * (height - padding * 2);
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="border rounded bg-slate-50"
    >
      <polyline
        fill="none"
        stroke="#0b1220"
        strokeWidth="1.5"
        points={points}
      />
    </svg>
  );
}

export const Route = createFileRoute("/kpis")({ component: Kpis });
